/**
 * El seguimiento comercial de una cotización, del lado de la pantalla.
 *
 * Antes una cotización tenía dos finales visibles: se convertía en orden, o se
 * vencía en silencio. Todo lo que se perdía se perdía sin rastro, así que ABCD
 * no tenía cómo saber **por qué** pierde.
 *
 * Esto NO reemplaza a la vigencia: son dos cosas distintas que conviven.
 *   vigencia → si el precio todavía se puede prometer (sale de la fecha)
 *   estado   → qué contestó el cliente (lo carga una persona)
 *
 * Una cotización puede estar *vencida* y *aceptada* al mismo tiempo — el cliente
 * dijo que sí sobre un precio que ya hay que recotizar—, y esconder cualquiera
 * de las dos deja a alguien tomando una decisión sin la mitad del cuadro.
 */

export type QuoteOutcome = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'LOST' | 'NO_RESPONSE';
export type QuoteLostReason =
  | 'PRICE'
  | 'TRANSIT_TIME'
  | 'COMPETITOR'
  | 'CUSTOMER_CANCELLED'
  | 'OTHER';

export const OUTCOME_LABEL: Record<QuoteOutcome, string> = {
  DRAFT: 'Borrador',
  SENT: 'Enviada',
  ACCEPTED: 'Aceptada',
  LOST: 'Perdida',
  NO_RESPONSE: 'Sin respuesta',
};

/**
 * El color dice de un vistazo qué hay que atender.
 *
 * `SENT` va en azul —lo que espera respuesta es donde está la plata en juego— y
 * `DRAFT` en gris, porque todavía no salió y no hay nada que perseguir. Perdida
 * y sin respuesta comparten el mismo apagado a propósito: las dos son finales y
 * ninguna merece un rojo de alarma, que se reserva para lo que hay que corregir.
 */
export const OUTCOME_TONE: Record<QuoteOutcome, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SENT: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  LOST: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  NO_RESPONSE: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
};

export const LOST_REASON_LABEL: Record<QuoteLostReason, string> = {
  PRICE: 'Precio',
  TRANSIT_TIME: 'Tiempo de tránsito',
  COMPETITOR: 'Se lo llevó otro agente',
  CUSTOMER_CANCELLED: 'El cliente desistió',
  OTHER: 'Otro motivo',
};

export const LOST_REASONS: QuoteLostReason[] = [
  'PRICE',
  'TRANSIT_TIME',
  'COMPETITOR',
  'CUSTOMER_CANCELLED',
  'OTHER',
];

/**
 * A dónde puede ir cada estado. Espeja la regla del servidor.
 *
 * Se duplica a propósito y con un motivo acotado: **esconder los botones que
 * van a fallar**. La validación de verdad la hace el backend —es la única que
 * no se puede saltear— y si las dos se separan, lo peor que pasa es que un
 * botón visible devuelva un error explicado. Al revés (confiar solo en el
 * servidor) la pantalla ofrecería acciones que rebotan.
 */
const TRANSITIONS: Record<QuoteOutcome, QuoteOutcome[]> = {
  DRAFT: ['SENT'],
  SENT: ['ACCEPTED', 'LOST', 'NO_RESPONSE', 'DRAFT'],
  ACCEPTED: ['SENT'],
  LOST: ['SENT', 'ACCEPTED'],
  NO_RESPONSE: ['SENT', 'ACCEPTED', 'LOST'],
};

export const nextOutcomes = (from: QuoteOutcome): QuoteOutcome[] => TRANSITIONS[from] ?? [];

/** Cerradas: ya no están en juego. */
export const isClosedOutcome = (o: QuoteOutcome): boolean =>
  o === 'ACCEPTED' || o === 'LOST' || o === 'NO_RESPONSE';

/**
 * Qué le pasó de verdad, mirando también dónde está el embarque.
 *
 * Espeja la regla del servidor, y hace falta acá por lo mismo que allá: el
 * seguimiento lo carga una persona y el estado lo mueve la operación. Cuando se
 * separan manda la operación — **no se embarca lo que el cliente no aprobó**.
 *
 * Sin esto la tabla y el tablero contaban distinto sobre las mismas filas: una
 * entregada cuyo seguimiento quedó en "Enviada" aparecía como enviada en la
 * lista y como aceptada en el resumen. Siete contra ocho, sin forma de saber
 * cuál creer.
 */
export function effectiveOutcome(quote: {
  status: string;
  quoteOutcome?: QuoteOutcome;
}): QuoteOutcome {
  const anotado = quote.quoteOutcome ?? 'DRAFT';
  if (quote.status === 'QUOTE' || quote.status === 'CANCELLED') return anotado;
  return 'ACCEPTED';
}

/**
 * Qué dice el botón que lleva a cada estado.
 *
 * En verbo y desde quien lo aprieta —"El cliente aceptó"— y no el nombre del
 * estado. Un menú que dice "Aceptada / Perdida / Sin respuesta" obliga a
 * traducir; uno que dice qué pasó se lee sin pensar.
 */
export const OUTCOME_ACTION: Record<QuoteOutcome, string> = {
  DRAFT: 'Volver a borrador',
  SENT: 'Marcar como enviada',
  ACCEPTED: 'El cliente aceptó',
  LOST: 'Se perdió',
  NO_RESPONSE: 'Nunca contestaron',
};
